import { ZoneFormCache, ZoneFormComponentProps, getLatestZone } from '../types'
import { Button } from 'components/Button/Button'
import Dropdown from 'components/Dropdown/Dropdown'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  selectMissionEditingZone,
  selectMissionInfo,
  selectMissionTempZone,
  selectSavingZoneProgress,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_SCRUBBER_BRUSH_SPEED,
  DEFAULT_TURBINE_SPEED,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
} from 'utils/constants'
import { ICleaningZone, RootState } from 'utils/interfaces'

import { resolveZoneEquipmentSetup } from './utils'

import style from './ZoneDetails.module.css'

const edgeAngleOptions = Array.from({ length: 7 }, (_, i) => {
  const displayVal = 60 + i * 10
  const serverVal = 180 - displayVal
  return { value: serverVal, text: `${displayVal}°` }
})

const borderLapsOptions = Array.from({ length: 51 }, (_, i) => {
  return { value: i, text: i.toString() }
})

const ZoneCoverageSettings = ({
  onSaveClicked,
  zone,
  getCacheValue,
  setCacheValue,
  getAllCacheValues,
  setCalculationMode,
}: ZoneFormComponentProps) => {
  const { t } = useTranslation()
  const editingZone = useSelector(selectMissionEditingZone)
  const tempZone = useSelector(selectMissionTempZone)
  const savingProgress = useSelector(selectSavingZoneProgress)
  const cleaningZones = useSelector((state: RootState) => state.mission.cleaningZones)
  const missionInfo = useSelector(selectMissionInfo)

  const zoneToUse = editingZone || zone
  const latestZone = getLatestZone(cleaningZones, zoneToUse?.properties?.zone_id)
  const finalZone = latestZone || zoneToUse

  const getInitialValue = <K extends keyof ZoneFormCache>(key: K, defaultValue: ZoneFormCache[K]): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    if (cached !== undefined && cached !== null) {
      return cached
    }
    if (
      (key === 'swathSequenceMode' ||
        key === 'bordersNum' ||
        key === 'maxEdgeAngle' ||
        key === 'maxEdgeAngleForBorders') &&
      finalZone
    ) {
      const setup = (finalZone as ICleaningZone)?.properties?.setup
      if (setup) {
        if (key === 'swathSequenceMode' && setup.swath_sequence_mode !== undefined) {
          return setup.swath_sequence_mode as ZoneFormCache[K]
        }
        if (key === 'bordersNum' && setup.borders_num !== undefined) {
          return setup.borders_num as ZoneFormCache[K]
        }
        if (setup.main_thresholds) {
          if (key === 'maxEdgeAngle' && setup.main_thresholds.max_edge_angle !== undefined) {
            return setup.main_thresholds.max_edge_angle as ZoneFormCache[K]
          }
          if (key === 'maxEdgeAngleForBorders' && setup.main_thresholds.max_edge_angle_for_borders !== undefined) {
            return setup.main_thresholds.max_edge_angle_for_borders as ZoneFormCache[K]
          }
        }
      }
    }
    return defaultValue
  }

  const [swathSequenceMode, setSwathSequenceMode] = useState<number>(() => {
    return (getInitialValue('swathSequenceMode', 0) as number) ?? 0
  })
  const [bordersNum, setBordersNum] = useState<number>(() => {
    return (getInitialValue('bordersNum', 0) as number) ?? 0
  })
  const [maxEdgeAngle, setMaxEdgeAngle] = useState<number>(() => {
    return (getInitialValue('maxEdgeAngle', 60.0) as number) ?? 60.0
  })
  const [maxEdgeAngleForBorders, setMaxEdgeAngleForBorders] = useState<number>(() => {
    return (getInitialValue('maxEdgeAngleForBorders', 80.0) as number) ?? 80.0
  })

  useEffect(() => {
    setCacheValue?.('swathSequenceMode', swathSequenceMode)
    setCacheValue?.('bordersNum', bordersNum)
    setCacheValue?.('maxEdgeAngle', maxEdgeAngle)
    setCacheValue?.('maxEdgeAngleForBorders', maxEdgeAngleForBorders)
  }, [swathSequenceMode, bordersNum, maxEdgeAngle, maxEdgeAngleForBorders, setCacheValue])

  const zoneIdRef = useRef<number | undefined>(zoneToUse?.properties?.zone_id)

  useEffect(() => {
    const currentZoneId = zoneToUse?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId

      const setup = finalZone ? (finalZone as ICleaningZone)?.properties?.setup : null
      if (setup) {
        const swathSequenceModeValue = setup.swath_sequence_mode ?? 0
        const bordersNumValue = setup.borders_num ?? 0
        const maxEdgeAngleValue = setup.main_thresholds?.max_edge_angle ?? 60.0
        const maxEdgeAngleForBordersValue = setup.main_thresholds?.max_edge_angle_for_borders ?? 80.0

        setSwathSequenceMode(swathSequenceModeValue)
        setBordersNum(bordersNumValue)
        setMaxEdgeAngle(maxEdgeAngleValue)
        setMaxEdgeAngleForBorders(maxEdgeAngleForBordersValue)

        setCacheValue?.('swathSequenceMode', swathSequenceModeValue)
        setCacheValue?.('bordersNum', bordersNumValue)
        setCacheValue?.('maxEdgeAngle', maxEdgeAngleValue)
        setCacheValue?.('maxEdgeAngleForBorders', maxEdgeAngleForBordersValue)
      } else {
        setSwathSequenceMode(0)
        setBordersNum(0)
        setMaxEdgeAngle(60.0)
        setMaxEdgeAngleForBorders(80.0)

        setCacheValue?.('swathSequenceMode', 0)
        setCacheValue?.('bordersNum', 0)
        setCacheValue?.('maxEdgeAngle', 60.0)
        setCacheValue?.('maxEdgeAngleForBorders', 80.0)
      }
    }
  }, [zoneToUse?.properties?.zone_id, finalZone, setCacheValue])

  const savingProgressInWork = useMemo(() => {
    return savingProgress === PROGRESS_TYPES.WORK
  }, [savingProgress])

  const getZoneFieldsFromCacheOrZone = () => {
    const cache = getAllCacheValues?.()
    const fallbackZone = tempZone || editingZone || zone
    const properties = (fallbackZone as ICleaningZone)?.properties

    return {
      zoneName: cache?.name || properties?.name || 'New Zone',
      zoneDescription: cache?.description || properties?.description || '',
      zoneType: (cache?.selectedZoneType || properties?.zone_type || MISSION_ZONE_TYPES.CLEANING) as MISSION_ZONE_TYPES,
      cleaningSpeed:
        cache?.cleaningSpeed !== undefined
          ? cache.cleaningSpeed
          : properties?.cleaning_speed ?? missionInfo?.cleaning_speed,
      transportSpeed:
        cache?.transportSpeed !== undefined
          ? cache.transportSpeed
          : properties?.transport_speed ?? missionInfo?.transport_speed,
      isBorderCleaning:
        cache?.isBorderCleaning !== undefined ? cache.isBorderCleaning : properties?.is_border_cleaning ?? false,
    }
  }

  const onSave = (keepEditing?: boolean) => {
    const cache = getAllCacheValues?.()
    const zoneToUse = tempZone || editingZone || zone
    const properties = (zoneToUse as ICleaningZone)?.properties
    const equipment_setup = resolveZoneEquipmentSetup(cache, zoneToUse)
    const setup = {
      swath_sequence_mode: swathSequenceMode,
      borders_num: bordersNum,
      main_thresholds: {
        max_edge_angle: maxEdgeAngle,
        max_edge_angle_for_borders: maxEdgeAngleForBorders,
      },
    }
    const zoneFields = getZoneFieldsFromCacheOrZone()

    return onSaveClicked({
      ...zoneFields,
      equipment_setup,
      setup,
      keepEditing,
    })
  }

  const onRecalculate = async () => {
    try {
      await onSave(true)
      if (zoneToUse?.properties?.zone_id) {
        setCalculationMode?.('single')
        await store.dispatch(missionThunks.startCalculatingRoute(zoneToUse.properties.zone_id))
      }
    } catch (error) {
      console.error('Failed to save or recalculate route:', error)
    }
  }

  const onCancelClicked = () => {
    store.dispatch(missionThunks.cancelEditingZone())
  }

  const resetToDefaults = () => {
    setSwathSequenceMode(0)
    setBordersNum(0)
    setMaxEdgeAngle(60.0)
    setMaxEdgeAngleForBorders(100.0)

    setCacheValue?.('swathSequenceMode', 0)
    setCacheValue?.('bordersNum', 0)
    setCacheValue?.('maxEdgeAngle', 60.0)
    setCacheValue?.('maxEdgeAngleForBorders', 100.0)
  }
  const swathSequenceOptions = [
    { value: 0, text: t('zone_details.swath_sequence_mode_0') || 'AUTO' },
    { value: 1, text: t('zone_details.swath_sequence_mode_1') || 'Border coverage goes first' },
    { value: 2, text: t('zone_details.swath_sequence_mode_2') || 'Central coverage goes first' },
  ]

  const maxEdgeAngleOption = edgeAngleOptions.find(opt => opt.value === maxEdgeAngle) || {
    value: maxEdgeAngle,
    text: `${180 - maxEdgeAngle}°`,
  }
  const maxEdgeAngleForBordersOption = edgeAngleOptions.find(opt => opt.value === maxEdgeAngleForBorders) || {
    value: maxEdgeAngleForBorders,
    text: `${180 - maxEdgeAngleForBorders}°`,
  }
  const borderLapsOption = borderLapsOptions.find(opt => opt.value === bordersNum) || {
    value: bordersNum,
    text: bordersNum.toString(),
  }

  const setup = finalZone ? (finalZone as ICleaningZone)?.properties?.setup : null
  const originalSwathSequenceMode = setup?.swath_sequence_mode ?? 0
  const originalBordersNum = setup?.borders_num ?? 0
  const originalMaxEdgeAngle = setup?.main_thresholds?.max_edge_angle ?? 60.0
  const originalMaxEdgeAngleForBorders = setup?.main_thresholds?.max_edge_angle_for_borders ?? 80.0

  const isSwathSequenceModeChanged = swathSequenceMode !== originalSwathSequenceMode
  const isBordersNumChanged = bordersNum !== originalBordersNum
  const isMaxEdgeAngleChanged = maxEdgeAngle !== originalMaxEdgeAngle
  const isMaxEdgeAngleForBordersChanged = maxEdgeAngleForBorders !== originalMaxEdgeAngleForBorders

  return (
    <div className={style.root}>
      <div className={style.content} style={{ flex: '0 1 auto' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', fontWeight: '500' }}>
          {t('mission.coverage_strategy')}
        </h2>
        <div style={{ marginTop: '8px', marginBottom: '16px' }}>
          <Dropdown
            noShadow
            className={isSwathSequenceModeChanged ? style.changedField : ''}
            options={swathSequenceOptions}
            currentOption={swathSequenceOptions.find(opt => opt.value === swathSequenceMode)}
            onClickOption={opt => {
              const val = Number(opt.value)
              setSwathSequenceMode(val)
              setCacheValue?.('swathSequenceMode', val)
            }}
            description={t('zone_details.swath_sequence_mode')}
          />
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
            {t('zone_details.swath_sequence_mode_desc') || 'Swath gluing order: which swath type comes first'}
          </div>
        </div>

        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <Dropdown
            noShadow
            className={isBordersNumChanged ? style.changedField : ''}
            options={borderLapsOptions}
            currentOption={borderLapsOption}
            onClickOption={opt => {
              const val = Number(opt.value)
              setBordersNum(val)
              setCacheValue?.('bordersNum', val)
            }}
            description={t('zone_details.borders_num')}
          />
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
            {t('zone_details.borders_num_desc') || 'Number of border swaths'}
          </div>
        </div>

        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <Dropdown
            noShadow
            className={isMaxEdgeAngleChanged ? style.changedField : ''}
            options={edgeAngleOptions}
            currentOption={maxEdgeAngleOption}
            onClickOption={opt => {
              const val = Number(opt.value)
              setMaxEdgeAngle(val)
              setCacheValue?.('maxEdgeAngle', val)
            }}
            description={t('zone_details.max_edge_angle')}
          />
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
            {t('zone_details.max_edge_angle_desc') || 'Maximum angle between connected edges'}
          </div>
        </div>

        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <Dropdown
            noShadow
            className={isMaxEdgeAngleForBordersChanged ? style.changedField : ''}
            options={edgeAngleOptions}
            currentOption={maxEdgeAngleForBordersOption}
            onClickOption={opt => {
              const val = Number(opt.value)
              setMaxEdgeAngleForBorders(val)
              setCacheValue?.('maxEdgeAngleForBorders', val)
            }}
            description={t('zone_details.max_edge_angle_for_borders')}
          />
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
            {t('zone_details.max_edge_angle_for_borders_desc') || 'Maximum angle between connected edges for borders'}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px' }}>
          <Button.Outlined
            className={style.button}
            style={{ marginLeft: 0 }}
            onClick={resetToDefaults}
            disabled={savingProgressInWork}
          >
            {t('common.reset')}
          </Button.Outlined>
          <Button.Standart
            className={style.button}
            style={{ marginLeft: 0 }}
            onClick={onRecalculate}
            disabled={savingProgressInWork}
            loading={savingProgressInWork}
          >
            {t('mission.calculate')}
          </Button.Standart>
        </div>
      </div>
      <div className={style.buttons} style={{ justifyContent: 'flex-end', marginTop: 'auto' }}>
        <Button.Outlined
          className={style.button}
          style={{ marginLeft: 0 }}
          onClick={onCancelClicked}
          disabled={savingProgressInWork}
        >
          {t('common.close')}
        </Button.Outlined>
      </div>
    </div>
  )
}

export default ZoneCoverageSettings
